import { useTranslation } from '@starsoft/common/hooks';
import styles from './styles.module.scss';
import { useTransactionById } from '@starsoft/common/hooks/transactions/useTransactionById';
import { useMemo } from 'react';
import { TransactionsCommonMappingModalContentDetailsActionRow } from './Row';
import { TransactionTransfer } from '@starsoft/common/models/transaction/transfer';

/**
 * TransactionsCommonMappingModalContentDetailsActions Component
 * This component is responsible for rendering the action rows related to transaction transfers.
 * It displays either a list of action rows based on transaction data or skeletons while loading.
 *
 * @returns {JSX.Element} The rendered component displaying action rows or skeletons.
 *
 * @example
 * // Usage example:
 * <TransactionsCommonMappingModalContentDetailsActions />
 */
export function TransactionsCommonMappingModalContentDetailsActions() {
  // Hook to handle translations for the 'common' namespace
  const { t } = useTranslation('common');
  // Hook to fetch transaction data by ID and handle loading state
  const { transaction, isLoading } = useTransactionById();

  /**
   * Actions Memoized Component
   * This memoized component maps over the transaction transfers and renders an action row for each transfer.
   * It uses the transfer ID as a key for each row.
   */
  const Actions = useMemo(
    () =>
      transaction?.transaction?.transfers?.map(transfer => (
        <TransactionsCommonMappingModalContentDetailsActionRow
          key={`transaction-transfer-action-${transfer?.id}`}
          transfer={transfer}
          transaction={transaction?.transaction}
        />
      )),
    [transaction],
  );

  /**
   * Skeletons Memoized Component
   * This memoized component generates a list of skeleton action rows to display while the data is loading.
   * It creates 5 skeleton rows with unique keys.
   */
  const Skeletons = useMemo(
    () =>
      Array.from({ length: 5 }).map((_, index) => (
        <TransactionsCommonMappingModalContentDetailsActionRow
          key={`transaction-transfer-action-skeleton-${index}`}
          transfer={{} as TransactionTransfer}
          isLoading
        />
      )),
    [],
  );

  // Render the component with a title and either the action rows or skeletons based on the loading state
  return (
    <div className={styles.container}>
      <h2 className={styles.container__title}>
        {t('transactions_mapping_title')}
      </h2>
      <div className={styles.container__card}>
        {isLoading ? Skeletons : Actions}
      </div>
    </div>
  );
}
